import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { needsNativeOAuth, signInWithProviderNative, type NativeOAuthOutcome } from "@/lib/oauth";
import { reportAppleAuthRejection } from "@/lib/apple-auth-diagnostics.functions";

/**
 * Sign in with Apple.
 *
 * Two code paths, one public API:
 *
 * 1. **Native iOS (Capacitor)** — uses the real `AuthenticationServices`
 *    sheet exposed by the `SignInWithApple` Capacitor plugin
 *    (`@capacitor-community/apple-sign-in`), then exchanges Apple's
 *    `identityToken` for a Supabase session with `signInWithIdToken`. This is
 *    the flow Apple requires for App Review guideline 4.8; no web view, no
 *    fake button.
 * 2. **Web / Android** — keeps the existing behaviour and uses the managed
 *    Apple OAuth provider through `lovable.auth.signInWithOAuth("apple")`.
 *
 * Both paths land on the same Supabase user record, so an account created on
 * the web with Apple is the same account on device (Apple always returns a
 * stable `sub`, even behind Hide My Email / private relay addresses), which is
 * what keeps us from creating duplicate profiles.
 *
 * Apple only sends `email` / `givenName` / `familyName` on the *first*
 * authorization, so the name is best-effort backfilled into the profile and
 * never required afterwards.
 *
 * Tokens are never logged or rendered — only coarse error codes bubble up.
 */

export type AppleSignInOutcome =
  | { status: "success" }
  | { status: "redirected" }
  | { status: "canceled" }
  | { status: "error"; code: AppleErrorCode };

/** Stable codes the UI maps to localized copy (`auth.appleErrors.*`). */
export type AppleErrorCode =
  | "canceled"
  | "no_identity_token"
  | "provider_disabled"
  | "network"
  | "credential_revoked"
  | "unknown";

interface AppleNativePlugin {
  authorize(options?: {
    clientId?: string;
    redirectURI?: string;
    scopes?: string;
    nonce?: string;
    state?: string;
  }): Promise<{
    response?: {
      identityToken?: string;
      authorizationCode?: string;
      email?: string | null;
      givenName?: string | null;
      familyName?: string | null;
      user?: string | null;
    };
  }>;
}

/** Official App ID / bundle identifier. Apple keys the credential on this. */
export const APPLE_CLIENT_ID = "online.inquira.app";
/** Public https callback used by the web variant of the plugin (never localhost). */
export const APPLE_REDIRECT_URI = "https://www.inquira.online/auth/callback";
/**
 * Deep-link OAuth fallback for Apple is intentionally disabled: iOS must always
 * use the native sheet, and Android uses Google/email instead.
 */
const NATIVE_WEB_OAUTH_FALLBACK_ENABLED: boolean = false;

function iosPlatform(): boolean {
  if (typeof window === "undefined") return false;
  const cap = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  return cap?.getPlatform?.() === "ios";
}

/** Plugin already registered on the Capacitor bridge, if any. */
function bridgeApplePlugin(): AppleNativePlugin | null {
  if (typeof window === "undefined") return null;
  const cap = (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } }).Capacitor;
  const plugin = cap?.Plugins?.["SignInWithApple"] as AppleNativePlugin | undefined;
  return plugin && typeof plugin.authorize === "function" ? plugin : null;
}

/**
 * Resolves the native Apple plugin on iOS.
 *
 * `window.Capacitor.Plugins` is only populated for plugins the JS layer has
 * registered, so relying on it alone made the native sheet silently unavailable
 * inside the wrapper — the app then fell through to a browser redirect, which
 * is what reviewers saw as an error. Importing the package registers the
 * bridge proxy explicitly.
 */
async function nativeApplePlugin(): Promise<AppleNativePlugin | null> {
  if (!iosPlatform()) return null;
  const existing = bridgeApplePlugin();
  if (existing) return existing;
  try {
    const mod = (await import("@capacitor-community/apple-sign-in")) as unknown as {
      SignInWithApple?: AppleNativePlugin;
    };
    const plugin = mod.SignInWithApple;
    return plugin && typeof plugin.authorize === "function" ? plugin : null;
  } catch {
    return null;
  }
}

/** True when the real native Apple sheet is available (iOS wrapper). */
export function isNativeAppleAvailable(): boolean {
  return iosPlatform();
}

/** Non-sensitive breadcrumbs so device/TestFlight issues are diagnosable. */
function log(event: string, detail?: Record<string, string | boolean>) {
  // Never include tokens, codes, emails or names here.
  console.info(`[apple-auth] ${event}`, detail ?? {});
}

function platform(): "ios" | "android" | "web" | "unknown" {
  if (typeof window === "undefined") return "unknown";
  const value = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor?.getPlatform?.();
  if (value === "ios" || value === "android") return value;
  return value ? "unknown" : "web";
}

function diagnosticCategory(
  err: unknown,
): "provider_disabled" | "audience_mismatch" | "credential_revoked" | "network" | "unknown" {
  const message = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  if (message.includes("audience") || message.includes("aud ") || message.includes("client_id")) {
    return "audience_mismatch";
  }
  const code = classify(err);
  return code === "canceled" || code === "no_identity_token" ? "unknown" : code;
}

function reportRejection(input: {
  stage: "native_authorize" | "session_exchange";
  category:
    | "provider_disabled"
    | "audience_mismatch"
    | "credential_revoked"
    | "network"
    | "no_identity_token"
    | "unknown";
  tokenPresent: boolean;
  noncePresent: boolean;
  authorizationCodePresent: boolean;
}) {
  void reportAppleAuthRejection({ data: { ...input, platform: platform() } }).catch(() => undefined);
}

/**
 * Apple ties the nonce it signs into the identity token, and Supabase
 * re-checks it. Apple expects the **SHA-256 hash** of the nonce in the
 * request; Supabase expects the **raw** value and hashes it itself.
 */
function randomNonce(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(value: string): Promise<string> {
  const subtle = typeof crypto !== "undefined" ? crypto.subtle : undefined;
  if (!subtle) return value; // Extremely old webview: fall back to the raw nonce.
  const digest = await subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

function classify(err: unknown): AppleErrorCode {
  const message = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  if (!message) return "unknown";
  if (
    message.includes("cancel") ||
    message.includes("1001") ||
    message.includes("aborted") ||
    message.includes("com.apple.authenticationservices.authorizationerror error 1001")
  )
    return "canceled";
  if (message.includes("revoked") || message.includes("expired")) return "credential_revoked";
  if (message.includes("provider") && (message.includes("not") || message.includes("disabled")))
    return "provider_disabled";
  // Backend Apple client/audience mismatch (bundle id not accepted): the user
  // can do nothing about it, so present it as "temporarily unavailable".
  if (message.includes("audience") || message.includes("aud ") || message.includes("client_id"))
    return "provider_disabled";
  if (message.includes("invalid") && message.includes("token")) return "credential_revoked";
  if (message.includes("network") || message.includes("fetch") || message.includes("offline")) return "network";
  return "unknown";
}

/**
 * Best-effort: store the display name Apple hands us on the very first
 * authorization. Never blocks sign-in and never overwrites an existing name.
 */
async function backfillAppleName(givenName?: string | null, familyName?: string | null) {
  const name = [givenName, familyName].filter(Boolean).join(" ").trim();
  if (!name) return;
  try {
    const { data } = await supabase.auth.getUser();
    const userId = data?.user?.id;
    if (!userId) return;
    const { data: profile } = await supabase
      .from("profiles")
      .select("display_name")
      .eq("user_id", userId)
      .maybeSingle();
    if (profile?.display_name) return;
    await supabase.from("profiles").update({ display_name: name }).eq("user_id", userId);
    log("name_backfilled");
  } catch {
    // Non-fatal: the account works without a name.
  }
}

export async function signInWithApple(redirectUri?: string): Promise<AppleSignInOutcome> {
  const plugin = await nativeApplePlugin();

  if (plugin) {
    const rawNonce = randomNonce();
    const hashedNonce = await sha256Hex(rawNonce);
    let identityToken: string | undefined;
    let givenName: string | null | undefined;
    let familyName: string | null | undefined;
    log("native_authorize_started", { client_id: APPLE_CLIENT_ID });
    try {
      const result = await plugin.authorize({
        // iOS ignores these (the entitlement supplies the App ID); they keep the
        // plugin's web variant on a real https callback instead of localhost.
        clientId: APPLE_CLIENT_ID,
        redirectURI: APPLE_REDIRECT_URI,
        scopes: "email name",
        nonce: hashedNonce,
      });
      identityToken = result?.response?.identityToken ?? undefined;
      givenName = result?.response?.givenName;
      familyName = result?.response?.familyName;
      log("native_authorize_returned", {
        has_token: Boolean(identityToken),
        // Apple only sends these on the first authorization.
        first_authorization: Boolean(givenName || familyName),
      });
    } catch (err) {
      const code = classify(err);
      log(code === "canceled" ? "native_authorize_canceled" : "native_authorize_failed", { code });
      if (code !== "canceled") {
        reportRejection({
          stage: "native_authorize",
          category: diagnosticCategory(err),
          tokenPresent: false,
          noncePresent: Boolean(rawNonce),
          authorizationCodePresent: false,
        });
      }
      return { status: code === "canceled" ? "canceled" : "error", code } as AppleSignInOutcome;
    }
    if (!identityToken) {
      log("native_authorize_no_token");
      reportRejection({
        stage: "native_authorize",
        category: "no_identity_token",
        tokenPresent: false,
        noncePresent: Boolean(rawNonce),
        authorizationCodePresent: false,
      });
      return { status: "error", code: "no_identity_token" };
    }

    const { error } = await supabase.auth.signInWithIdToken({
      provider: "apple",
      token: identityToken,
      nonce: rawNonce,
    });
    if (error) {
      const code = classify(error);
      log("session_exchange_failed", { code });
      reportRejection({
        stage: "session_exchange",
        category: diagnosticCategory(error),
        tokenPresent: true,
        noncePresent: Boolean(rawNonce),
        authorizationCodePresent: false,
      });
      return { status: "error", code };
    }
    log("session_established", { native: true });
    await backfillAppleName(givenName, familyName);
    return { status: "success" };
  }

  // Keep the approved native sheet path above unchanged. Missing iOS native
  // support must fail safely rather than silently opening a browser redirect.
  if (iosPlatform() && !plugin) return { status: "error", code: "provider_disabled" };

  if (NATIVE_WEB_OAUTH_FALLBACK_ENABLED && needsNativeOAuth()) {
    log("native_web_oauth_started", { ios: iosPlatform() });
    const outcome: NativeOAuthOutcome = await signInWithProviderNative("apple");
    if (outcome.status === "success") return { status: "success" };
    if (outcome.status === "canceled") return { status: "canceled" };
    return { status: "error", code: outcome.code };
  }

  // Web / Android on the hosted site: existing managed Apple OAuth.
  try {
    log("web_oauth_started");
    const result = await lovable.auth.signInWithOAuth("apple", {
      redirect_uri: redirectUri ?? (typeof window !== "undefined" ? window.location.origin : undefined),
    });
    if (result.redirected) return { status: "redirected" };
    if (result.error) {
      const code = classify(result.error);
      log("web_oauth_failed", { code });
      return code === "canceled" ? { status: "canceled" } : { status: "error", code };
    }
    log("session_established", { native: false });
    return { status: "success" };
  } catch (err) {
    const code = classify(err);
    log("web_oauth_failed", { code });
    return code === "canceled" ? { status: "canceled" } : { status: "error", code };
  }
}
